import { useCallback, useEffect, useState, useRef } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Toolbar, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../../api/api.configuration';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Item } from 'devextreme-react/form';
import { SysIAnalitosResulTipoListas } from '../../../types/types.analitos';
import { newToast } from '../../../components';

export const ResultListByAnalytes = ({ IdAnalito }: { IdAnalito: number }) => {
  const dataRef = useRef<DataGrid>(null);
  const { getResultadosTipoListaByAnalito, newResultadoTipoLista, updateResultadoTipoLista, deleteResultadoTipoLista } = useApiConfiguration();
  const [resultados, setResultados] = useState<SysIAnalitosResulTipoListas[]>([]);

  // Configuración del DataGrid con CustomStore
  const customData = new CustomStore({
    key: 'Rtl_Id',
    load: () => 
    {
      return getResultadosTipoListaByAnalito(IdAnalito)
        .then((res) => ({
          data: res,
          totalCount: res.totalCount,
          summary: res.summary,
          groupCount: res.groupCount,
        }))
        .catch(() => {
          throw new Error('Error de carga de datos');
        });
    },
    insert: async (values) => {
      const newData: SysIAnalitosResulTipoListas = {
        Rtl_Id: 0, // ID generado en la BD
        Rtl_An_Id: IdAnalito,
        Rtl_Resultado: values.Rtl_Resultado || '',
        Rtl_Descripcion: values.Rtl_Descripcion || '',
        Rtl_Abreviatura: values.Rtl_Abreviatura || '',
      };
      return newResultadoTipoLista(newData)
        .then((res) => {
          if (res === 105) {
            dataRef.current?.instance.refresh();
            return res;
          }
          throw new Error('No se pudo insertar el resultado tipo lista');
        })
        .catch((error) => {
          console.error('Error insertando resultado tipo lista:', error);
          throw new Error('Error insertando resultado tipo lista');
        });
    },
    update: async (key, values) => {
      return updateResultadoTipoLista(key, values)
        .then((res) => {
          if (res) {
            dataRef.current?.instance.refresh();
          }
        })
        .catch(() => {
          throw new Error('Error actualizando resultado tipo lista');
        });
    },
    remove: async (key) => {
      return deleteResultadoTipoLista(key)
        .then((res) => {
          if (res) {
            dataRef.current?.instance.refresh();
          }
        })
        .catch(() => {
          throw new Error('Error eliminando resultado tipo lista');
        });
    },
  });

  // 💡 Usar un nuevo DataSource cada vez que se actualizan los datos
  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    dataRef.current?.instance.addRow();

    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        dataRef.current?.instance.cellValue(newRowIndex, 'Rtl_Resultado', `${rowData.Rtl_Resultado} - Copia`);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rtl_Descripcion', rowData.Rtl_Descripcion);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rtl_Abreviatura', rowData.Rtl_Abreviatura);
      }
    }, 100);

    newToast('INFO', 'Resultado duplicado. Complete la edición para guardar.', 3);
  }, []);

  return (
    <DataGrid dataSource={customDataSrc} ref={dataRef} width={'100%'} columnAutoWidth={true} hoverStateEnabled={true} showBorders={true}>
      <Scrolling columnRenderingMode='standard' useNative={true} scrollByContent={true} showScrollbar='always' />
      <SearchPanel visible={true} />
      <Editing mode='form' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} 
        texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
        <Form>
          <Item dataField='Rtl_Resultado' />
          <Item dataField='Rtl_Descripcion' />
          <Item dataField='Rtl_Abreviatura' />
        </Form>
      </Editing>
      <Toolbar>
        <Item name='searchPanel' />
        <Item name='addRowButton' />
      </Toolbar>
      <Column caption='ID' dataField='Rtl_Id' alignment='center' visible={true} allowEditing={false} />
      <Column caption='RESULTADO' dataField='Rtl_Resultado' alignment='center' visible={true} allowEditing={true}>
        <RequiredRule />
      </Column>
      <Column caption='DESCRIPCIÓN' dataField='Rtl_Descripcion' alignment='center' visible={true} allowEditing={true}>
        <RequiredRule />
      </Column>
      <Column caption='ABREVIATURA' dataField='Rtl_Abreviatura' alignment='center' visible={true} allowEditing={true}>
        <RequiredRule />
      </Column>
      <Column type='buttons' caption={'ACCIÓN'} width={200}>
        <Butt name='edit' />
        <Butt name='delete' />
        <Butt icon='copy' hint='Duplicar resultado' onClick={handleDuplicate} />
      </Column>
    </DataGrid>
  );
};
